import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@tkpod/ui';
import { visibleInOrder, type DisplayRuleContext } from '@tkpod/shared';
import { mountOptionsPreview, type PublicOptionsConfig } from '@tkpod/options-widget';
import { choicesOf, rulesOf, type ProductOptionRow } from './product-option-model';
import type { PricingBlank } from './OptionEditorModal';
import { customSizeConfigSchema } from '@tkpod/shared';

export interface PreviewAxis {
  optionTypeId: string;
  name: string;
  uiType: string;
  isRequired: boolean;
  isActive: boolean;
  values: Array<{
    slug: string;
    displayName: string;
    name: string;
    swatchUrl: string | null;
    isActive: boolean;
  }>;
}

export function OptionsLivePreview({
  templateId,
  options,
  axes,
  blockConfig,
  surchargeMode,
  blockEnabled,
  pricingBlanks = [],
}: {
  templateId: string;
  options: ProductOptionRow[];
  axes: PreviewAxis[];
  blockConfig: unknown;
  surchargeMode: string;
  blockEnabled: boolean;
  pricingBlanks?: PricingBlank[];
}) {
  const host = useRef<HTMLDivElement>(null);
  const [context, setContext] = useState<DisplayRuleContext>({ selections: {}, variantAxes: {}, quantity: 1 });
  const visibleKeys = useMemo(() => {
    const active = options.filter((option) => option.isActive);
    return new Set(
      visibleInOrder(active, context, (option) =>
        option.displayRules == null ? [] : rulesOf(option.displayRules),
      ).map((option) => option.key),
    );
  }, [options, context]);
  const config = useMemo(
    () => previewConfig(templateId, options, axes, blockConfig, surchargeMode, blockEnabled, pricingBlanks),
    [templateId, options, axes, blockConfig, surchargeMode, blockEnabled, pricingBlanks],
  );
  const serialized = JSON.stringify(config);

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const onState = (event: Event) => {
      const detail = (event as CustomEvent<DisplayRuleContext>).detail;
      if (detail?.selections) setContext(detail);
    };
    node.addEventListener('tk:options-state', onState);
    const unmount = mountOptionsPreview(node, JSON.parse(serialized) as PublicOptionsConfig);
    return () => {
      node.removeEventListener('tk:options-state', onState);
      unmount();
    };
  }, [serialized]);

  return (
    <Card>
      <div className="space-y-3 px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">Live preview</h3>
          <p className="mt-1 text-xs text-text-secondary">
            The same fields the customer sees, including the option you are editing.
          </p>
        </div>
        <ul className="space-y-1 text-xs text-text-secondary">
          {options.filter((option) => option.isActive).map((option) => {
            const visible = visibleKeys.has(option.key);
            return (
              <li key={option.id} className="flex items-center gap-2">
                <span aria-hidden="true">{visible ? '◉' : '○'}</span>
                <span>{visible ? 'Visible' : 'Hidden'}</span>
                <span className="text-text-primary">{option.displayName || option.key}</span>
              </li>
            );
          })}
        </ul>
        <div ref={host} />
      </div>
    </Card>
  );
}

function previewConfig(
  templateId: string,
  options: ProductOptionRow[],
  axes: PreviewAxis[],
  blockConfig: unknown,
  surchargeMode: string,
  blockEnabled: boolean,
  pricingBlanks: PricingBlank[] = [],
): PublicOptionsConfig {
  const variantAxes = axes
    .filter((axis) => axis.isActive)
    .map((axis) => ({
      optionTypeId: axis.optionTypeId,
      name: axis.name,
      uiType: axis.uiType,
      isRequired: axis.isRequired,
      values: axis.values
        .filter((value) => value.isActive)
        .map((value) => ({
          key: value.slug,
          label: value.displayName || value.name,
          swatchUrl: value.swatchUrl,
        })),
    }));

  const charge = surchargeMode !== 'DISABLED';
  const block = unchargedBlock(blockConfig, charge);
  return {
    uptId: templateId,
    blockEnabled,
    surchargeMode,
    blockConfig: block,
    options: options
      .filter((option) => option.isActive)
      .map((option) => ({
        key: option.key,
        displayName: option.displayName || option.key,
        optionType: option.optionType,
        role: option.role,
        helpText: option.helpText,
        placeholder: option.placeholder,
        defaultValue: option.defaultValue,
        isRequired: option.isRequired,
        config: option.config,
        values: choicesOf(option.values),
        displayRules: option.displayRules,
        boundOptionTypeId: option.boundOptionTypeId,
        lineItemLabel: option.lineItemLabel,
        hiddenInCart: option.hiddenInCart,
        priceDeltaMode: charge ? option.priceDeltaMode : 'NONE',
        priceDeltaCents: charge ? option.priceDeltaCents : null,
        priceDeltaPercent: charge ? option.priceDeltaPercent : null,
      })),
    variantAxes,
    variants: charge ? previewVariants(options, pricingBlanks) : [],
    variantsTruncated: false,
  };
}

function unchargedBlock(blockConfig: unknown, charge: boolean): unknown {
  if (charge || !blockConfig || typeof blockConfig !== 'object' || Array.isArray(blockConfig)) {
    return blockConfig ?? {};
  }
  const block = blockConfig as { quantity?: { mode?: string; tiers?: unknown[] } };
  if (!block.quantity || typeof block.quantity !== 'object') return blockConfig;
  return { ...block, quantity: { ...block.quantity, mode: 'SINGLE', tiers: [] } };
}

function previewVariants(options: ProductOptionRow[], blanks: PricingBlank[]) {
  const priced = options.find((option) => {
    if (option.optionType !== 'CUSTOM_SIZE' || !option.isActive) return false;
    const parsed = customSizeConfigSchema.safeParse(option.config ?? {});
    return parsed.success && Boolean(parsed.data.pricingVariantId);
  });
  if (!priced) return [];
  const parsed = customSizeConfigSchema.safeParse(priced.config ?? {});
  const blankId = parsed.success ? parsed.data.pricingVariantId : null;
  const blank = blanks.find((item) => item.id === blankId);
  if (!blank) return [];
  const cents = Math.round(Number(blank.portalUnitCost ?? blank.cost) * 100);
  if (!Number.isFinite(cents)) return [];
  return [
    {
      productVariantId: 'preview',
      externalVariantId: null,
      blankVariantId: blank.id,
      sku: blank.sku,
      title: blank.sku,
      priceCents: cents,
      formulaRateCents: cents,
      axes: {},
    },
  ];
}
